import React, { useContext, useEffect } from 'react';
import { SchedulePostView, SchedulePostViewProps } from './SchedulePost.view';
import { PostEditorContext } from '../../states/PostEditorProvider';
import { PostEditorGroupDataContext } from '../../states/PostEditorGroupDataProvider';
import { useDidMount } from '../../../../hooks/useDidMount';
import { FormElementAction, FormElementValueType } from '../../../../models/basic-objects';
import { ActiveCustomizeIndexContext } from '../../states/ActiveCustomizeIndexProvider';
import { PostStatus, PostTiming } from '../../../../models/graphql';
import { showToast } from '../../../../helpers/showToast';
import { PostEditorContextEnum } from 'models/post-editor';
import { AppContext } from 'providers/AppProvider';
import { Tooltip } from 'components/actions/Tooltip';

export type SchedulePostControllerProps = {
  formInstance: SchedulePostViewProps['formInstance'];
  // extend your SchedulePostViewProps
};

export const SchedulePostController = ({ formInstance }: SchedulePostControllerProps): JSX.Element => {
  // Put All your methods and logic here
  const { state, dispatch } = useContext(PostEditorContext);
  const customizeIndex = useContext(ActiveCustomizeIndexContext);
  const [tabsState] = useContext(PostEditorGroupDataContext);
  const { postEditorContext } = useContext(AppContext);
  const isBulkImport = postEditorContext.type === PostEditorContextEnum.import;
  const { postGroupIndex } = tabsState;
  const { postGroups, loading } = state;
  const {
    customizeSchedulePerProfile,
    customizeContentPerProfile,
    groupPostAttributes: {
      scheduling: { postTiming: globalPostTiming },
      postStatus
    },
    posts
  } = postGroups[postGroupIndex];
  const postTiming =
    typeof customizeIndex === 'number' && customizeSchedulePerProfile
      ? posts[customizeIndex].scheduling.postTiming
      : globalPostTiming;

  const postNowDisabled =
    (typeof customizeIndex === 'number' && customizeContentPerProfile
      ? posts[customizeIndex].postStatus === PostStatus.DRAFT
      : postStatus === PostStatus.DRAFT) || isBulkImport;

  const postGroupName =
    typeof customizeIndex === 'number' && customizeSchedulePerProfile
      ? `postGroup.${postGroupIndex}.customize.${customizeIndex}`
      : `postGroup.${postGroupIndex}`;

  const isPostTimingGuard = (value: FormElementValueType): value is PostTiming => {
    if (typeof value !== 'string') throw new Error('value is not of type string');
    return value in PostTiming;
  };

  const action: FormElementAction = (value, complete) => {
    if (!isPostTimingGuard(value)) throw new Error('Value is not a proper PostTiming enum');
    complete();
    dispatch({
      type: 'setPostTiming',
      payload: {
        postGroupIndex,
        postTiming: value,
        customizeIndex
      }
    });
  };

  useEffect(() => {
    if (postNowDisabled && postTiming === PostTiming.NOW) {
      showToast({
        severity: 'warn',
        summary: 'Scheduling Conflict',
        detail: 'Draft posts can\'t be set to "Post Now".'
      });
    }
  }, [postNowDisabled, postTiming]);

  useDidMount(
    () => () => formInstance.unregister(`${postGroupName}.postTiming`, { keepValue: false, keepDefaultValue: false })
  );
  const tooltip = isBulkImport ? (
    <Tooltip
      target={`#bulkImportPostNow_${postGroupIndex}`}
      position={'right'}
      pointer={true}
      type={'inlined'}
      autoZIndex={false}
      style={{ zIndex: 2000 }}>
      <>Post Now is not available in bulk mode</>
    </Tooltip>
  ) : undefined;

  const viewProps: SchedulePostViewProps = {
    action,
    formInstance,
    loading,
    postTiming,
    postGroupName,
    postNowDisabled,
    tooltip
  };

  return <SchedulePostView {...viewProps} />;
};
